import { useState, useRef, useEffect, RefObject } from "react"
import { TrackProps } from "./audio-player.component"

interface UseAudioPlayer {
  play: () => void
  pause: () => void
  next: () => void
  prev: () => void
  playing: boolean
  displayNextPrev: boolean
  currentTrack: TrackProps
  audioRef: RefObject<HTMLAudioElement>
  duration: string
  playTime: string
}

export const useAudioHook = (tracks: TrackProps[]): UseAudioPlayer => {
  const [playing, setPlaying] = useState<boolean>(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const audioRef = useRef<HTMLAudioElement>(null)
  const [duration, setDuration] = useState<string>("")
  const [playTime, setPlayTime] = useState<string>("")

  const formatTime = (time: number): string => {
    const hours = ~~(time / 3600)
    const minutes = ~~((time % 3600) / 60)
    const seconds = ~~time % 60

    let result = ""
    if (hours > 0) {
      result += hours.toString() + ":" + (minutes < 10 ? "0" : "")
    }
    result += minutes.toString() + ":" + (seconds < 10 ? "0" : "")
    result += seconds.toString()

    return result
  }

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.oncanplaythrough = () => {
        setDuration(formatTime(audioRef.current!.duration))
      }
      audioRef.current.ontimeupdate = () => {
        setPlayTime(formatTime(audioRef.current!.currentTime))
      }
    }
  }, [])

  useEffect(() => {
    if (audioRef.current?.load) {
      audioRef.current.load()
      if (playing) {
        play()
      }
    }
  }, [currentIndex])

  const next = (): void => {
    if (currentIndex < tracks.length - 1) {
      setCurrentIndex(currentIndex + 1)
    } else {
      setCurrentIndex(0)
    }
  }

  const prev = (): void => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    } else {
      setCurrentIndex(tracks.length - 1)
    }
  }

  const play = async (): Promise<void> => {
    if (audioRef.current) {
      try {
        await audioRef.current.play()
        setPlaying(true)
      } catch (error) {
        setPlaying(false)
      }
    }
  }

  const pause = (): void => {
    audioRef.current!.pause()
    setPlaying(false)
  }

  return {
    displayNextPrev: tracks.length > 1,
    currentTrack: tracks[currentIndex],
    duration,
    playTime,
    playing,
    play,
    pause,
    next,
    prev,
    audioRef,
  }
}
